import { Container, Graphics, Text } from 'pixi.js';
import type { Scene } from '../core/Scene';
import type { InputSnapshot } from '../input/actions';
import type { GameContext } from '../GameContext';
import { Palette } from '../render/Palette';
import { purchase, type ShopItem } from '../sim/run/shop';
import type { RunState } from '../sim/run/RunState';
import type { Player } from '../sim/entities/Player';

const PANEL_W = 460;
const ROW_H = 64;

/** Entrance shop. Pauses the sim; controller and keyboard navigable. */
export class ShopOverlay implements Scene {
  readonly root = new Container();
  readonly blocksUpdateBelow = true;

  private focus = 0;
  private rows: Container[] = [];
  private rowTexts: Text[] = [];
  private focusRing = new Graphics();
  private gemsText: Text;
  private acceptDelay = 8;
  private panelX: number;
  private panelY: number;

  constructor(
    private readonly ctx: GameContext,
    private readonly stock: ShopItem[],
    private readonly runState: RunState,
    private readonly player: Player,
  ) {
    const w = ctx.app.screen.width;
    const h = ctx.app.screen.height;
    const panelH = 120 + stock.length * ROW_H;
    this.panelX = (w - PANEL_W) / 2;
    this.panelY = (h - panelH) / 2;

    this.root.addChild(new Graphics().rect(0, 0, w, h).fill({ color: 0x05060c, alpha: 0.75 }));
    this.root.addChild(new Graphics()
      .roundRect(this.panelX, this.panelY, PANEL_W, panelH, 10)
      .fill(Palette.hudPanel)
      .stroke({ width: 2, color: Palette.gold, alpha: 0.6 }));

    const title = new Text({
      text: 'WAYSIDE PEDDLER',
      style: {
        fontFamily: 'monospace', fontSize: 22, fontWeight: 'bold',
        fill: Palette.gold, letterSpacing: 3,
      },
    });
    title.anchor.set(0.5, 0);
    title.position.set(w / 2, this.panelY + 18);
    this.root.addChild(title);

    this.gemsText = new Text({
      text: '',
      style: { fontFamily: 'monospace', fontSize: 14, fill: Palette.gem },
    });
    this.gemsText.anchor.set(0.5, 0);
    this.gemsText.position.set(w / 2, this.panelY + 48);
    this.root.addChild(this.gemsText);

    this.root.addChild(this.focusRing);
    stock.forEach((item, i) => {
      const row = new Container();
      row.position.set(this.panelX + 16, this.panelY + 82 + i * ROW_H);
      const text = new Text({
        text: '',
        style: {
          fontFamily: 'monospace', fontSize: 14, fill: Palette.hudText, lineHeight: 20,
        },
      });
      row.addChild(text);
      this.rowTexts.push(text);
      this.rows.push(row);
      this.root.addChild(row);
      this.refreshRow(i, item);
    });

    const hint = new Text({
      text: 'buy  [E / Ⓐ]      leave  [Esc / Ⓑ]',
      style: { fontFamily: 'monospace', fontSize: 12, fill: Palette.hudDim },
    });
    hint.anchor.set(0.5);
    hint.position.set(w / 2, this.panelY + panelH - 20);
    this.root.addChild(hint);
    this.refreshGems();
  }

  private refreshGems(): void {
    this.gemsText.text = `◆ ${Math.round(this.runState.gemsHeld)}`;
  }

  private refreshRow(i: number, item: ShopItem): void {
    const text = this.rowTexts[i]!;
    const out = item.stock <= 0;
    text.text = `${item.name}  —  ${item.desc}\n${out ? 'SOLD OUT' : `◆ ${item.price}   (${item.stock} left)`}`;
    text.alpha = out ? 0.4 : 1;
  }

  enter(): void {}
  exit(): void {}

  update(_dt: number, input: InputSnapshot): void {
    if (this.acceptDelay > 0) {
      this.acceptDelay--;
      return;
    }
    if (input.navY !== 0 && this.rows.length > 0) {
      this.focus = (this.focus + input.navY + this.rows.length) % this.rows.length;
    }
    if (input.confirmPressed) {
      const item = this.stock[this.focus];
      if (item && purchase(item, this.runState, this.player)) {
        this.refreshRow(this.focus, item);
        this.refreshGems();
      }
    }
    if (input.cancelPressed || input.pausePressed) {
      this.ctx.scenes.pop();
    }
  }

  render(_alpha: number): void {
    const g = this.focusRing;
    g.clear();
    const row = this.rows[this.focus];
    if (!row) return;
    g.roundRect(row.position.x - 6, row.position.y - 8, PANEL_W - 20, ROW_H - 6, 6)
      .stroke({ width: 2, color: Palette.focus });
  }
}
